import {
  connectionNotice,
  message,
  type AccountInfo,
  type ConnectionState,
  type Message,
} from '@sl/core';
import { Banner, Button, ExternalLinkButton, type BannerTone } from '../../ui';
import { asSentence, messageText } from '../messages';
import { testId } from '../testId';
import { ADS_MANAGER_COPY, META_TOOL } from './adsManager';
import { SHELL_COPY } from './copy';
import { useMessages } from './shellContext';
import styles from './AppShell.module.css';

// The one band under the top bar — REQ-264 AC2 and REQ-287 AC1, said ONCE for the whole account instead of
// once per row.
//
// `AccountInfo.connection.state` is the account's own condition, so this band belongs to the shell and rides
// on every screen (`shell: true`). The two halves of every state are kept apart on purpose:
//
//   · WHAT HAPPENED comes off the wire — `connection.reason`, or the catalogue key for the state when the
//     server sent none. Never retyped here, so the product and the server cannot drift. **T-210: that
//     resolution is `@sl/core/queries`' `connectionNotice` now** (T-208 / D-1078), so the web and the phone
//     cannot differ about which sentence a state gets, or about which states raise a band at all.
//   · WHAT IT MEANS FOR YOUR ADS AND WHAT TO DO is the app's own sentence, because no message key carries
//     it, and w17 §3 requires all three parts in that order (D-540: the words stay in the apps).
//
// The "Account problem" case (REQ-045's account-level delivery status) is the one that matters most: nothing
// runs and nothing spends until it is fixed at Meta, the server stops returning verdicts for the rows, and
// the fix lives in a Meta tool we do not reimplement (REQ-264 AC1). The pointer out never carries an
// identifier (`META_TOOL`, REQ-002 AC3) and is disabled on a demo account with its own reason.

/**
 * The app's own half of each band: its tone, its icon, its test id and the sentence that says what the state
 * means for your ads and what to do (D-540 — those words have no message key and stay here).
 *
 * **Which states raise a band is NOT this table's decision** (T-210): that is `CONNECTION_BAND_STATES` in
 * `@sl/core/queries`, and `connectionNotice(...).band` is what the render below asks. `ok` and
 * `syncing_first_time` raise none — one is fine, the other is the shell's own loading state (REQ-002 AC2),
 * which the freshness label already says — and `ConnectionBand.test.tsx` holds this table's keys against
 * core's list, so a sixth state cannot arrive on one side alone.
 */
const BAND: Partial<
  Record<
    ConnectionState,
    {
      tone: BannerTone;
      icon: 'alert-triangle' | 'clock' | 'cloud-off' | 'lock';
      testID: string;
      body: string;
      tool?: keyof typeof META_TOOL;
    }
  >
> = {
  account_problem: {
    tone: 'watch',
    icon: 'alert-triangle',
    testID: 'account.problem',
    body: 'Nothing can run or spend until it is fixed, so while it lasts we stop saying how your campaigns are doing. Your campaigns, budgets and history are untouched.',
    tool: 'billing',
  },
  // T-211 — this sentence has a name now (`SHELL_COPY.notAffectedHere`) because REQ-283 AC2's three-hour
  // banner says the same thing, and two spellings of one sentence is how a product starts disagreeing with
  // itself. The words have not changed and neither has which states use them.
  token_invalid: {
    tone: 'watch',
    icon: 'lock',
    testID: 'account.connection',
    body: SHELL_COPY.notAffectedHere,
  },
  permission_missing: {
    tone: 'watch',
    icon: 'lock',
    testID: 'account.connection',
    body: SHELL_COPY.notAffectedHere,
  },
  rate_limited: {
    tone: 'info',
    icon: 'clock',
    testID: 'state.rate-limited',
    body: 'Your ads and your spending are not affected. The figures on screen are the last ones we were allowed to read.',
  },
  unreachable: {
    tone: 'watch',
    icon: 'cloud-off',
    testID: 'account.connection',
    body: 'Your ads keep running as normal. We will pick the numbers up again as soon as Meta answers.',
  },
};

/** The states whose band is raised — what a test walks, and what a page's own banner must not repeat. */
export const BAND_STATES = Object.keys(BAND) as ConnectionState[];

/**
 * REQ-264 AC2 asks for "the name of the place in Meta where it is fixed". The catalogue sentence already
 * says to fix it there; this adds the names Meta's own menu uses, so the user knows what to look for.
 */
export const WHERE_TO_FIX = `Meta calls those two places ${META_TOOL.billing.name} and ${META_TOOL.accountQuality.name}.`;

export interface ConnectionBandProps {
  account: AccountInfo;
  /** Set by the app when the client says the device has no connection (`state.offline-banner`). */
  offline?: boolean;
  /**
   * **T-211 — the instant §12.2 does not carry.** REQ-286 AC2's sentence is "Meta is limiting how often we
   * can check — next update at 2:50 pm", and the band's own `connection` block is `{ state, reason }` with no
   * instant in it at all: adding one there is a contract change (API §12.2, D-1065). So the shell passes
   * `liveState(...).rateLimited` in — built from §2.3's `rateLimit.nextAttemptAt` or §1.6's
   * `warning.rate_limited` param, and structurally never from this band (D-1078 (d)) — and the rate-limited
   * title prefers it. With no instant anywhere it is `null` and the band keeps the reason, which names no
   * time and needs none.
   *
   * It is a prop rather than a read inside this component for the reason the whole row exists: `meta` is read
   * ONCE per render of the shell (`useShellLive`), so the band and the caption above it cannot be told
   * different things about the same answer.
   */
  rateLimited?: Message | null;
  /** w17 §4 / §6 level 3: the offline band states the clock time of what is on screen where there is one
   *  (`state.offline_since`), and `state.offline` where nothing has been read yet. `offlineState(...)`'s. */
  offlineMessage?: Message | null;
}

export function ConnectionBand({
  account,
  offline = false,
  rateLimited = null,
  offlineMessage = null,
}: ConnectionBandProps) {
  const messages = useMessages();

  if (offline) {
    return (
      <Banner
        tone="watch"
        icon="cloud-off"
        title={asSentence(messageText(offlineMessage ?? message('state.offline'), messages))}
        testID={testId('state', 'offline-banner')}
        className={styles.band}
      >
        {SHELL_COPY.offlineBody}
      </Banner>
    );
  }

  // T-210 — one resolution of §12.2's `connection` for both apps: the server's own `reason`, else the
  // catalogue key for the state, and whether the state raises a band at all.
  const notice = connectionNotice(account.connection);
  const spec = notice?.band ? BAND[notice.state] : undefined;
  if (!notice || !spec) return null;

  // The bold lead and the sentence after it run together on one line (w17 §3), so a catalogue sentence that
  // carries no terminal punctuation gets a full stop here. The words themselves are never touched.
  const instant = notice.state === 'rate_limited' ? rateLimited : null;
  const what = asSentence(messageText(instant ?? notice.reason, messages));
  const problem = notice.state === 'account_problem';

  return (
    <Banner
      tone={spec.tone}
      icon={spec.icon}
      title={what}
      testID={spec.testID}
      className={styles.band}
      actions={
        problem ? (
          account.capabilities.canOpenAdsManager ? (
            <ExternalLinkButton
              variant="secondary"
              size="sm"
              icon="arrow-right"
              href={META_TOOL.billing.url}
              testID={testId('account', 'problem', 'open')}
            >
              {ADS_MANAGER_COPY.open}
            </ExternalLinkButton>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              icon="arrow-right"
              disabled
              testID={testId('account', 'problem', 'open')}
            >
              {ADS_MANAGER_COPY.open}
            </Button>
          )
        ) : undefined
      }
    >
      {spec.body}
      {problem ? ` ${WHERE_TO_FIX}` : null}
    </Banner>
  );
}
